import { useState, useMemo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Award,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  AlertTriangle,
  Search,
  History,
  FileSpreadsheet,
  CheckCircle2,
} from 'lucide-react'
import { formatCurrency, formatNumber } from '../../../utils'
import {
  usePremiacao,
  usePremiacaoDetalhe,
  usePremiacaoConferencias,
  usePremiacaoHistorico,
  obterPeriodoQuinzenaAnterior,
} from '../hooks/usePremiacao'
import type { PremiacaoResumoTime, PremiacaoConferenciaTime } from '../tipos/premiacao'
import { premiacaoService } from '../services/premiacaoService'
import { exportarRelatorioColaboradoresPdf } from '../services/exportarRelatorioColaboradoresPdf'
import { timeService } from '../../times/services/time'
import { performanceService } from '../../performance/services/performanceService'
import type { PerformanceColaboradorLinha } from '../../performance/tipos/performance'
import {
  HoverTooltipNomeColaborador,
  HoverTooltipIntegrantesTime,
} from '../../../componentesCompartilhados/HoverTooltipHorasPerformance'

const MESES = [
  { v: 1, l: 'Janeiro' },
  { v: 2, l: 'Fevereiro' },
  { v: 3, l: 'Março' },
  { v: 4, l: 'Abril' },
  { v: 5, l: 'Maio' },
  { v: 6, l: 'Junho' },
  { v: 7, l: 'Julho' },
  { v: 8, l: 'Agosto' },
  { v: 9, l: 'Setembro' },
  { v: 10, l: 'Outubro' },
  { v: 11, l: 'Novembro' },
  { v: 12, l: 'Dezembro' },
]

function anosSelect(): number[] {
  const y = new Date().getFullYear()
  return [y, y - 1, y - 2, y - 3, y - 4]
}

export default function PremiacaoAdministrador() {
  const defaults = obterPeriodoQuinzenaAnterior()
  const [ano, setAno] = useState(defaults.ano)
  const [mes, setMes] = useState(defaults.mes)
  const [numeroQuinzena, setNumeroQuinzena] = useState(defaults.quinzena)
  const [filtroAtivo, setFiltroAtivo] = useState({ ano: defaults.ano, mes: defaults.mes, quinzena: defaults.quinzena })
  const [timeExpandido, setTimeExpandido] = useState<number | undefined>()
  const [modalRelatorio, setModalRelatorio] = useState(false)
  const [mostrarHistorico, setMostrarHistorico] = useState(false)
  const queryClient = useQueryClient()

  const { data: resultado, isLoading: loadingPremiacao, refetch } = usePremiacao(
    filtroAtivo.ano,
    filtroAtivo.mes,
    filtroAtivo.quinzena,
  )
  const { data: detalhe, isLoading: loadingDetalhe } = usePremiacaoDetalhe(
    filtroAtivo.ano,
    filtroAtivo.mes,
    filtroAtivo.quinzena,
    timeExpandido,
  )
  const { data: conferencias = [] } = usePremiacaoConferencias(filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena)
  const { data: historico = [] } = usePremiacaoHistorico(24, mostrarHistorico)

  const { data: linhasRelatorio = [], isLoading: loadingRel } = useQuery(
    ['premiacao-relatorio', filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena],
    () => premiacaoService.obterRelatorioColaboradores(filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena),
    { enabled: modalRelatorio },
  )

  const totaisRelatorio = useMemo(() => {
    if (linhasRelatorio.length === 0)
      return null
    let horas = 0
    let premiacaoAlcancada = 0
    let premFinal = 0
    for (const r of linhasRelatorio) {
      horas += r.horasTrabalhadasPessoa
      premiacaoAlcancada += r.premiacaoCalculada
      premFinal += r.premiacaoFinal
    }
    return { horas, premiacaoAlcancada, premFinal }
  }, [linhasRelatorio])

  const exportarPdfRelatorio = useCallback(() => {
    if (linhasRelatorio.length === 0)
      return
    const mesLabel = MESES.find((m) => m.v === filtroAtivo.mes)?.l ?? String(filtroAtivo.mes)
    const titulo = `Relatório por colaborador — ${mesLabel}/${filtroAtivo.ano} · ${filtroAtivo.quinzena}ª quinzena`
    const nome = `premiacao-colaboradores-${filtroAtivo.ano}-${String(filtroAtivo.mes).padStart(2, '0')}-q${filtroAtivo.quinzena}.pdf`
    exportarRelatorioColaboradoresPdf(linhasRelatorio, nome, titulo)
  }, [linhasRelatorio, filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena])

  const mutPaga = useMutation(
    () => premiacaoService.marcarQuinzenaPaga(filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['premiacao'])
        queryClient.invalidateQueries(['premiacao-historico'])
      },
    },
  )

  const handleBuscar = () => {
    setTimeExpandido(undefined)
    setFiltroAtivo({ ano, mes, quinzena: numeroQuinzena })
  }

  const toggleTime = (timeId: number) => {
    setTimeExpandido(timeExpandido === timeId ? undefined : timeId)
  }

  const confPorTime = new Map<number, PremiacaoConferenciaTime>()
  conferencias.forEach((c) => confPorTime.set(c.timeId, c))

  const { data: timesConfig = [] } = useQuery(
    'times-configuracao',
    () => timeService.listarTimesConfiguracao(),
    { staleTime: 5 * 60 * 1000 },
  )

  const integrantesPorTimeId = useMemo(() => {
    const m = new Map<number, string>()
    for (const t of timesConfig) {
      const nomes = t.integrantes.map((i) => i.nome).filter(Boolean)
      if (nomes.length > 0)
        m.set(t.id, nomes.join(', '))
    }
    return m
  }, [timesConfig])

  const { data: performanceResumo } = useQuery(
    ['performance', 'premiacao-admin', resultado?.dataInicio, resultado?.dataFim],
    () => performanceService.obter(resultado!.dataInicio, resultado!.dataFim),
    { enabled: !!resultado?.dataInicio && !!resultado?.dataFim, staleTime: 2 * 60 * 1000 },
  )

  const performancePorPessoa = useMemo(() => {
    const m = new Map<number, PerformanceColaboradorLinha>()
    for (const c of performanceResumo?.colaboradores ?? [])
      m.set(c.pessoaId, c)
    return m
  }, [performanceResumo])

  const jaPaga = !!resultado?.dataPagamentoQuinzena

  return (
    <div className="space-y-6 max-w-6xl mx-auto px-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <Award className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Premiação</h1>
            {resultado?.quinzenaId != null && (
              <p className="text-xs text-gray-500">
                Quinzena #{resultado.quinzenaId}
                {jaPaga && resultado.dataPagamentoQuinzena
                  ? ` · Paga em ${new Date(resultado.dataPagamentoQuinzena).toLocaleString('pt-BR')}`
                  : !jaPaga
                    ? ' · Pagamento não registrado'
                    : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setMostrarHistorico(!mostrarHistorico)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <History className="w-4 h-4" />
            Histórico
          </button>
          <button
            type="button"
            onClick={() => setModalRelatorio(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Relatório colaboradores
          </button>
          {!jaPaga && filtroAtivo.ano > 0 && (
            <button
              type="button"
              onClick={() => mutPaga.mutate()}
              disabled={mutPaga.isLoading}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              Marcar quinzena como paga
            </button>
          )}
        </div>
      </div>

      {mostrarHistorico && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4 overflow-x-auto">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Últimas quinzenas</h2>
          <table className="w-full text-xs min-w-[640px]">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-600">
                <th className="py-2 pr-2">Período</th>
                <th className="py-2 pr-2">Pagamento</th>
                <th className="py-2 pr-2 text-center">Conferidos</th>
                <th className="py-2 pr-2 text-center">Divergências</th>
                <th className="py-2 text-center">Pendentes*</th>
              </tr>
            </thead>
            <tbody>
              {historico.map((h) => (
                <tr key={h.quinzenaId} className="border-b border-gray-100">
                  <td className="py-2 pr-2">{h.descricao}</td>
                  <td className="py-2 pr-2">
                    {h.dataPagamento ? new Date(h.dataPagamento).toLocaleDateString('pt-BR') : '—'}
                  </td>
                  <td className="py-2 text-center">{h.quantidadeConferidos}</td>
                  <td className="py-2 text-center">{h.quantidadeDivergencias}</td>
                  <td className="py-2 text-center">{h.quantidadeSemRegistroOuPendente}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-2">* Estimativa com base nos times com faturamento no período.</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Mês</label>
            <select
              value={mes}
              onChange={(e) => setMes(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 min-w-[140px]"
            >
              {MESES.map((m) => (
                <option key={m.v} value={m.v}>
                  {m.l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Ano</label>
            <select
              value={ano}
              onChange={(e) => setAno(Number(e.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            >
              {anosSelect().map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
          <div>
            <span className="block text-xs font-medium text-gray-600 mb-1">Quinzena</span>
            <div className="flex gap-4 items-center pt-1">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="qnz"
                  checked={numeroQuinzena === 1}
                  onChange={() => setNumeroQuinzena(1)}
                />
                1ª (dia 1–15)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="radio"
                  name="qnz"
                  checked={numeroQuinzena === 2}
                  onChange={() => setNumeroQuinzena(2)}
                />
                2ª (dia 16–fim)
              </label>
            </div>
          </div>
          <button
            onClick={handleBuscar}
            disabled={loadingPremiacao}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-500 rounded-lg hover:bg-brand-600 disabled:opacity-50"
          >
            <Search className="w-4 h-4" />
            Buscar
          </button>
          <button
            onClick={() => refetch()}
            disabled={loadingPremiacao}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingPremiacao ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loadingPremiacao ? (
        <div className="flex items-center justify-center h-32">
          <RefreshCw className="w-6 h-6 animate-spin text-brand-500" />
        </div>
      ) : resultado?.times && resultado.times.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Conferência</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Pessoas</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Hrs Trabalhadas</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Hrs Faturadas</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Valor Faturado</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">% Alcançado</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Premiação Total</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Premiação/Pessoa</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {resultado.times.map((time) => (
                <TimeRow
                  key={time.timeId}
                  time={time}
                  conferencia={confPorTime.get(time.timeId)}
                  integrantesTooltip={integrantesPorTimeId.get(time.timeId)}
                  performancePorPessoa={performancePorPessoa}
                  expandido={timeExpandido === time.timeId}
                  onToggle={() => toggleTime(time.timeId)}
                  detalhe={timeExpandido === time.timeId ? detalhe : undefined}
                  loadingDetalhe={loadingDetalhe && timeExpandido === time.timeId}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-theme-sm p-8 text-center text-gray-500">
          Nenhum faturamento encontrado para o período selecionado.
        </div>
      )}

      {modalRelatorio &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Relatório por colaborador</h3>
              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  type="button"
                  onClick={() =>
                    premiacaoService.baixarRelatorioCsv(filtroAtivo.ano, filtroAtivo.mes, filtroAtivo.quinzena)
                  }
                  className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Exportar CSV
                </button>
                <button
                  type="button"
                  onClick={exportarPdfRelatorio}
                  disabled={loadingRel || linhasRelatorio.length === 0}
                  className="text-sm px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Exportar PDF
                </button>
                <button
                  type="button"
                  onClick={() => setModalRelatorio(false)}
                  className="text-sm px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  Fechar
                </button>
              </div>
            </div>
            <div className="overflow-auto p-4">
              {loadingRel ? (
                <div className="flex justify-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin text-brand-500" />
                </div>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-gray-600">
                      <th className="py-2 pr-2">Time</th>
                      <th className="py-2 pr-2">Colaborador</th>
                      <th className="py-2 pr-2">Contratação</th>
                      <th className="py-2 text-right">Hrs trab.</th>
                      <th className="py-2 text-right">Vl. hora</th>
                      <th className="py-2 text-right">Teto</th>
                      <th className="py-2 text-right">% Alcançado</th>
                      <th className="py-2 text-right">Prem. Total</th>
                      <th className="py-2 text-right">Prem. final</th>
                      <th className="py-2 text-right">% teto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhasRelatorio.map((r, idx) => {
                      const linhaAtingiuTeto = r.atigiuTeto && r.teto > 0
                      return (
                      <tr
                        key={`${r.timeId}-${r.pessoaId}`}
                        className={`border-b border-gray-100 ${
                          linhaAtingiuTeto ? 'bg-green-50' : idx % 2 === 0 ? 'bg-gray-50/90' : 'bg-white'
                        }`}
                      >
                        <td className="py-2 pr-2">
                          {integrantesPorTimeId.get(r.timeId) ? (
                            <HoverTooltipIntegrantesTime
                              titulo={r.nomeTime}
                              textoIntegrantes={integrantesPorTimeId.get(r.timeId)!}
                            >
                              <span>{r.nomeTime}</span>
                            </HoverTooltipIntegrantesTime>
                          ) : (
                            r.nomeTime
                          )}
                        </td>
                        <td className="py-2 pr-2">
                          <HoverTooltipNomeColaborador
                            pessoaId={r.pessoaId}
                            nomeExibicao={r.nomeColaborador}
                            performancePorPessoa={performancePorPessoa}
                          />
                        </td>
                        <td className="py-2 pr-2">{r.tipoContratacao}</td>
                        <td className="py-2 text-right">{formatNumber(r.horasTrabalhadasPessoa)}</td>
                        <td className="py-2 text-right">{formatCurrency(r.valorHora)}</td>
                        <td className="py-2 text-right">{formatCurrency(r.teto)}</td>
                        <td className="py-2 text-right">{(r.percentualAlcancadoTime * 100).toFixed(2)}%</td>
                        <td className="py-2 text-right">{formatCurrency(r.premiacaoTotalTime)}</td>
                        <td
                          className={`py-2 text-right font-medium ${
                            linhaAtingiuTeto ? 'text-green-700' : 'text-gray-900'
                          }`}
                        >
                          {formatCurrency(r.premiacaoFinal)}
                        </td>
                        <td className="py-2 text-right">
                          {r.percentualDoTeto != null ? `${(r.percentualDoTeto * 100).toFixed(1)}%` : '—'}
                        </td>
                      </tr>
                      )
                    })}
                    {totaisRelatorio && (
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-900">
                        <td className="py-2 pr-2" colSpan={3}>
                          Totais
                        </td>
                        <td className="py-2 text-right">{formatNumber(totaisRelatorio.horas)}</td>
                        <td className="py-2 text-right">—</td>
                        <td className="py-2 text-right">—</td>
                        <td className="py-2 text-right">—</td>
                        <td className="py-2 text-right">{formatCurrency(totaisRelatorio.premiacaoAlcancada)}</td>
                        <td className="py-2 text-right text-green-800">{formatCurrency(totaisRelatorio.premFinal)}</td>
                        <td className="py-2 text-right">—</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

function badgeConferencia(c?: PremiacaoConferenciaTime) {
  if (!c || c.statusCodigo === 0)
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Pendente</span>
    )
  if (c.statusCodigo === 1)
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Conferido</span>
    )
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
      <AlertTriangle className="w-3 h-3" />
      Divergência
    </span>
  )
}

function TimeRow({
  time,
  conferencia,
  integrantesTooltip,
  performancePorPessoa,
  expandido,
  onToggle,
  detalhe,
  loadingDetalhe,
}: {
  time: PremiacaoResumoTime
  conferencia?: PremiacaoConferenciaTime
  integrantesTooltip?: string
  performancePorPessoa: Map<number, PerformanceColaboradorLinha>
  expandido: boolean
  onToggle: () => void
  detalhe: ReturnType<typeof usePremiacaoDetalhe>['data']
  loadingDetalhe: boolean
}) {
  return (
    <>
      <tr
        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={onToggle}
      >
        <td className="px-4 py-3 font-medium text-gray-900">
          {integrantesTooltip ? (
            <HoverTooltipIntegrantesTime titulo={time.nomeTime} textoIntegrantes={integrantesTooltip}>
              <span>{time.nomeTime}</span>
            </HoverTooltipIntegrantesTime>
          ) : (
            time.nomeTime
          )}
        </td>
        <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col gap-1">
            {badgeConferencia(conferencia)}
            {conferencia?.textoDivergencia ? (
              <span className="text-xs text-red-700 max-w-xs line-clamp-2" title={conferencia.textoDivergencia}>
                {conferencia.textoDivergencia}
              </span>
            ) : null}
          </div>
        </td>
        <td className="px-4 py-3 text-center text-gray-700">{time.quantidadePessoas}</td>
        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(time.horasTrabalhadasTime)}h</td>
        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(time.horasFaturadas)}h</td>
        <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(time.totalValorFaturado)}</td>
        <td className="px-4 py-3 text-right text-gray-700">{(time.percentualAlcancado * 100).toFixed(2)}%</td>
        <td className="px-4 py-3 text-right font-semibold text-green-700">{formatCurrency(time.premiacaoTotal)}</td>
        <td className="px-4 py-3 text-right font-semibold text-green-700">{formatCurrency(time.premiacaoPorPessoa)}</td>
        <td className="px-4 py-3 text-center">
          {expandido ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </td>
      </tr>
      {expandido && (
        <tr>
          <td colSpan={10} className="p-0">
            <div className="bg-gray-50 border-b border-gray-200 p-4 space-y-4">
              {loadingDetalhe ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-5 h-5 animate-spin text-brand-500" />
                </div>
              ) : detalhe ? (
                <>
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Faturamentos Enviados</h4>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-3 py-2 text-left font-medium text-gray-600">NF</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Data Envio</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Etapa</th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Projeto</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Minutos</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Valor</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalhe.faturamentos.map((f) => (
                            <tr key={f.idFaturamento} className="border-b border-gray-100">
                              <td className="px-3 py-2 text-gray-700">{f.nf || '-'}</td>
                              <td className="px-3 py-2 text-gray-700">
                                {f.dataEnvioAnalista ? new Date(f.dataEnvioAnalista).toLocaleDateString('pt-BR') : '-'}
                              </td>
                              <td className="px-3 py-2 text-gray-700">{f.etapaDescricao}</td>
                              <td className="px-3 py-2 text-gray-700">{f.projetoNome}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(f.totalMinutos)}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(f.valorTotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Colaboradores</h4>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-3 py-2 text-left font-medium text-gray-600">Nome</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Minutos</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Horas</th>
                            <th className="px-3 py-2 text-right font-medium text-gray-600">Valor Faturado</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detalhe.colaboradores.map((c) => (
                            <tr key={c.pessoaId} className="border-b border-gray-100">
                              <td className="px-3 py-2 font-medium text-gray-900">
                                <HoverTooltipNomeColaborador
                                  pessoaId={c.pessoaId}
                                  nomeExibicao={c.nome}
                                  performancePorPessoa={performancePorPessoa}
                                />
                              </td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(c.totalMinutos)}</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatNumber(c.horas)}h</td>
                              <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(c.valorFaturado)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {detalhe.individual && detalhe.individual.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Premiação Individual</h4>
                      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <th className="px-3 py-2 text-left font-medium text-gray-600">Colaborador</th>
                              <th className="px-3 py-2 text-center font-medium text-gray-600">Contratação</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Hrs Trab.</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Valor Hora</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Teto</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-600">Premiação</th>
                              <th className="px-3 py-2 text-center font-medium text-gray-600">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {detalhe.individual.map((ind) => (
                              <tr
                                key={ind.pessoaId}
                                className={`border-b border-gray-100 ${ind.sinalizado ? 'bg-red-50' : ind.atigiuTeto ? 'bg-amber-50' : ''}`}
                              >
                                <td className="px-3 py-2 font-medium text-gray-900">
                                  <div className="flex items-center gap-2">
                                    <HoverTooltipNomeColaborador
                                      pessoaId={ind.pessoaId}
                                      nomeExibicao={ind.nome}
                                      performancePorPessoa={performancePorPessoa}
                                    />
                                    {ind.sinalizado && (
                                      <span title={`${ind.tetoConsecutivoAtual} quinzenas consecutivas no teto`}>
                                        <AlertTriangle className="w-4 h-4 text-red-500" />
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 text-center">
                                  <span
                                    className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                      ind.tipoContratacao === 'PJ' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                                    }`}
                                  >
                                    {ind.tipoContratacao}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-right text-gray-700">{formatNumber(ind.horasTrabalhadasPessoa)}h</td>
                                <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(ind.valorHora)}</td>
                                <td className="px-3 py-2 text-right text-gray-700">{formatCurrency(ind.teto)}</td>
                                <td className="px-3 py-2 text-right font-semibold text-green-700">{formatCurrency(ind.premiacaoFinal)}</td>
                                <td className="px-3 py-2 text-center">
                                  {ind.atigiuTeto ? (
                                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                      Teto
                                    </span>
                                  ) : (
                                    <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                      OK
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
